/**
 * Pieces the FM safety screens share (FM Manager rebuild W6: C14–C18): the
 * response shapes of /fm/permits, /fm/drills and /fm/incidents, the refresh
 * every safety write does, the Done / Not required toggle and a row of action
 * buttons that say why when they cannot be pressed.
 */
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { routeRequestError } from '@/utils/validation';
import { httpStatusOf } from './ticketParts';

// ---------------------------------------------------------------------------
// Shapes (backend/routers/fm_permits.py)
// ---------------------------------------------------------------------------

export type PermitType = 'hot_work' | 'work_at_height' | 'confined_space' | 'electrical_isolation' | 'cold_work';
export type PermitState = 'requested' | 'active' | 'expired' | 'closed' | 'cancelled';
export type Answer = 'done' | 'not_required' | null;

export interface PermitCard {
  id: string; number: string; type: PermitType; type_label: string;
  site: string | null; location: string; work: string; contractor: string | null; workers: number | null;
  state: PermitState; valid_from: string | null; valid_to: string | null; revalidations: number;
  issuer: string | null; receiver: string | null; timezone: string;
  close_reason: string | null; closed_at: string | null;
}

export type PermitColumn = 'requested' | 'active' | 'expired' | 'closed';

export interface PermitsBoard {
  columns: Record<PermitColumn, PermitCard[]>;
  counts: Record<PermitColumn, number>;
  sites: { id: string; name: string }[];
  /** request: may raise permits (helpdesk:edit); a read-only viewer may not. */
  can: { issue: boolean; request?: boolean };
}

export interface PermitNeeds {
  fire_watch: boolean; standbys: boolean; loto: boolean; gas_test: boolean;
  /** FM Technician B34: hot work's fire-zone isolation step. */
  fire_zone?: boolean;
}

/** FM Technician B34 (D10): a manual confined-space entry's exception. */
export interface PermitException {
  state: 'none' | 'asked' | 'approved' | 'refused';
  rescue_plan: string | null; decided_by: string | null; decided_at: string | null;
}

export interface GasTest {
  at: string; lel_pct: number; o2_pct: number; h2s_ppm: number | null; co_ppm: number | null;
  by: string | null; safe: boolean;
}

export interface PermitDetail extends PermitCard {
  checklist: { item: string; answer: Answer }[];
  fire_watch_name: string | null; standbys: string[]; loto_ref: string | null;
  gas_tests: GasTest[];
  needs: PermitNeeds;
  issuer_signed_at: string | null; receiver_signed_at: string | null;
  receiver_signed_on: string | null; closed_by: string | null;
  can: { edit: boolean; issue: boolean; reject: boolean; revalidate: boolean; close: boolean; gas_test: boolean;
         /** B34: approve or refuse a manual confined-space entry (permit:issue). */
         confined_exception?: boolean };
  // ---- FM Technician R3 (B34): optional so older payloads still read.
  receiver_id?: string | null;
  space_kind?: string | null; space_kind_label?: string | null; manual_entry?: boolean;
  fire_zone_isolated?: boolean;
  exception?: PermitException;
  /** The receiver who signed on their own phone (D18). */
  receiver_signed_by?: string | null;
  insulation_test?: { value_mohm: number; tool_name: string; serial: string | null; at: string } | null;
}

export interface PermitTypeInfo { type: PermitType; label: string; checklist: string[]; needs: PermitNeeds }

export interface DrillSite {
  site_id: string; site: string; interval_months: number | null; last: string | null;
  next_due: string | null; days_to_next: number | null; timezone: string;
}
export interface DrillRow {
  id: string; site_id: string; site: string; date: string; type: string; status: string;
  evacuation_seconds: number | null; people: number | null; evacuated: number | null;
  observation: string | null; by: string | null;
}
export interface DrillsPage { sites: DrillSite[]; drills: DrillRow[] }

export type IncidentType = 'near_miss' | 'first_aid' | 'injury' | 'property_damage' | 'fire';
export interface IncidentRow {
  id: string; site: string; occurred_at: string; timezone: string; type: IncidentType;
  description: string; status: 'open' | 'closed'; permit: string | null;
  /** "Name hidden" when the reporter chose it and the caller is not the site's
   *  FM, safety officer or the reporter (FM Technician D21) — shown as given. */
  reported_by: string | null;
  hide_name?: boolean; photo_count?: number;
  /** Who has opened it (FM Technician A10, D11). */
  seen_by?: string[];
}
export interface IncidentsPage { rows: IncidentRow[]; can: { close: boolean; report?: boolean } }

export const INCIDENT_TYPES: [IncidentType, string][] = [
  ['near_miss', 'Near miss'], ['first_aid', 'First aid'], ['injury', 'Injury'],
  ['property_damage', 'Property damage'], ['fire', 'Fire'],
];
export const incidentLabel = (t: string) => INCIDENT_TYPES.find(([k]) => k === t)?.[1] ?? t;

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Every safety write re-reads every screen it can change: the board, the
 *  pop-up, incidents (a permit makes one when it expires), drills and home. */
export function useSafetyInvalidate() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['permits', 'permit', 'incidents', 'drills', 'home', 'shell']) {
      void qc.invalidateQueries({ queryKey: ['fm', k] });
    }
  };
}

/** The server's own words for a 4xx (they are written for people). */
export function refusal(err: unknown, fallback = "Couldn't do that. Try again."): string {
  const s = httpStatusOf(err);
  return s >= 400 && s < 500 ? routeRequestError(err, {})?.message ?? fallback : fallback;
}

export const NOT_ISSUER = 'Only a permit issuer can do this';

// ---------------------------------------------------------------------------
// UI bits
// ---------------------------------------------------------------------------

/** Done / Not required for one checklist line; pressing the chosen one again
 *  clears it. Read-only shows the answer as a pill. */
export function ChecklistLine({ item, answer, onChange, busy }: {
  item: string; answer: Answer; onChange?: (a: Answer) => void; busy?: boolean;
}) {
  const btn = (value: Exclude<Answer, null>, label: string) => (
    <button type="button" aria-pressed={answer === value} disabled={busy}
            data-testid={`check-${value}`}
            onClick={() => onChange?.(answer === value ? null : value)}
            className={`min-h-[44px] rounded-full border px-3 text-xs font-medium ${
              answer === value
                ? value === 'done' ? 'border-success-600 bg-success-50 text-success-700'
                  : 'border-line-strong bg-surface-muted text-content'
                : 'border-line bg-surface text-content-secondary hover:bg-surface-subtle'}`}>
      {label}
    </button>
  );
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-1.5" data-testid="check-line"
        data-answer={answer ?? 'none'}>
      <span className="min-w-0 flex-1 break-words text-sm text-content">{item}</span>
      {onChange ? (
        <span className="flex shrink-0 gap-1.5">{btn('done', 'Done')}{btn('not_required', 'Not required')}</span>
      ) : (
        <span className={`badge ${answer === 'done' ? 'bg-success-50 text-success-700'
          : 'bg-surface-muted text-content-secondary'}`}>
          {answer === 'done' ? 'Done' : answer === 'not_required' ? 'Not required' : '—'}
        </span>
      )}
    </li>
  );
}

export interface Act {
  key: string; label: string; onClick: () => void; why: string | null;
  tone?: 'primary' | 'danger' | 'secondary'; testid?: string;
}

/** Buttons that, when they cannot be pressed, say why — in the title and in
 *  words under the buttons (phones have no hover). */
export function ActionRow({ acts, busy }: { acts: Act[]; busy?: boolean }) {
  const why = acts.filter((a) => a.why);
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {acts.map((a) => (
          <button key={a.key} type="button" data-testid={a.testid}
                  className={`${a.tone === 'primary' ? 'btn-primary' : a.tone === 'danger' ? 'btn-danger' : 'btn-secondary'} min-h-[44px]`}
                  disabled={!!a.why || busy} title={a.why ?? undefined} onClick={a.onClick}>
            {a.label}
          </button>
        ))}
      </div>
      {why.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-content-muted" data-testid="why-disabled">
          {why.map((a) => <li key={a.key}>{a.label}: {a.why}</li>)}
        </ul>
      )}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs font-medium text-content-tertiary">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-content-muted">{hint}</span>}
    </label>
  );
}

export function InlineError({ text, testid = 'form-error' }: { text: string | null; testid?: string }) {
  if (!text) return null;
  return (
    <p role="alert" data-testid={testid}
       className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
      {text}
    </p>
  );
}

export const INPUT = 'input-field min-h-[44px]';
